import {
    Avatar,
    Colors,
    DIALOG_INITIAL_FOCUS_ATTRIBUTE,
    getPasswordManagerAttributes,
    Icon,
    Spinner,
    useAnchoredDropdownStyle,
    useModalPortalTarget,
} from '@amelio/ui-library';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { EmployeeSearchResultRow } from './EmployeeSearchResultRow';

import type { AssessmentEmployee } from '@/types/manager-assessment/Employee';
import type { CSSProperties, FocusEvent } from 'react';

import {
    ASSESSMENT_EMPLOYEES_HIERARCHY_QUERY_KEY,
    useGetAssessmentEmployees,
} from '@/api/cycle-evaluation-services/manager-assessment';
import { useGetUserById, useGetUserPermissions } from '@/api/directory';
import directoryService from '@/api/directory/directoryService';
import { DirectoryQueryKey } from '@/constants/QueryKeys';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useDebounce } from '@/hooks/useDebounce';
import {
    buildAssignProfileLocationState,
    buildAssignProfileSearch,
} from '@/navigation/assignProfileNavigation';
import { RouteName } from '@/navigation/types';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

interface EmployeeSelectorProps {
    selectedEmployeeId: string | null;
    preselectedEmployeeId?: string;
    /** Whether an employee without a competency profile can be picked. The 360 assessment needs the
     * profile to build its questionnaire; flows that do not (a thematic meeting, for instance) pass
     * `false` so every employee stays selectable. @default true */
    requireCompetencyProfile?: boolean;
    /** Whether focusing or clicking the empty field proposes a short list of employees, narrowed as
     * the name is typed. Without it, nothing is listed until a search is typed. @default false */
    suggestOnFocus?: boolean;
    /** Whether the search field takes the focus as soon as it is shown, for a flow whose next
     * question is typed. The field arrives with the caret in it and the list closed: that focus is
     * not a gesture, so it proposes nobody. Inside a modal dialog it also names the field as the
     * dialog's starting control, so the dialog's own focus pass lands there rather than on its
     * close button a frame later. @default false */
    focusOnMount?: boolean;
    onSelectEmployee: (employee: AssessmentEmployee) => void;
    onDeselectEmployee: () => void;
}

const RESULTS_LIST_MAX_HEIGHT = 400;
const RESULTS_LIST_VIEWPORT_MARGIN = 8;

// Whichever side the list opens on, it stops short of the window's edge and scrolls instead.
const getResultsListMaxHeight = ({ top, bottom }: CSSProperties) => {
    const distanceFromOpeningEdge = typeof top === 'number' ? top : bottom;
    if (typeof distanceFromOpeningEdge !== 'number') {
        return RESULTS_LIST_MAX_HEIGHT;
    }
    return `min(${RESULTS_LIST_MAX_HEIGHT}px, calc(100dvh - ${distanceFromOpeningEdge + RESULTS_LIST_VIEWPORT_MARGIN}px))`;
};

export const EmployeeSelector = ({
    selectedEmployeeId,
    preselectedEmployeeId,
    requireCompetencyProfile = true,
    suggestOnFocus = false,
    focusOnMount = false,
    onSelectEmployee,
    onDeselectEmployee,
}: EmployeeSelectorProps) => {
    const { t } = useAmelioTranslations();
    const { getJobTitle } = useCompanyInformation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [isSuggestionListOpen, setIsSuggestionListOpen] = useState(false);
    // Keep the picked employee locally so its card still renders after the search clears.
    const [selectedEmployee, setSelectedEmployee] = useState<AssessmentEmployee | null>(null);

    // The results leave the flow and are anchored to the field, exactly as the library's own Select
    // does and as the objective form's picker was fixed. A list left in the flow is trapped inside
    // the card that holds it — the card carries a backdrop blur, which opens a stacking context —
    // and the blocks below it draw over the list whatever its z-index. Reported by Maxime on the
    // meeting-scheduling modal, where the topic tiles covered the names.
    const fieldRef = useRef<HTMLDivElement>(null);
    const resultsPopoverRef = useRef<HTMLDivElement>(null);
    const resultsListRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Marks the one focus this component moves itself, so the handler below can tell it apart from
    // a focus the manager caused. Set and cleared around the call, which dispatches the event
    // synchronously: nothing else can ever fall inside it.
    const isMovingFocusItself = useRef(false);

    useEffect(() => {
        if (!focusOnMount) {
            return;
        }
        isMovingFocusItself.current = true;
        inputRef.current?.focus();
        isMovingFocusItself.current = false;
    }, [focusOnMount]);

    // The effect above is enough outside a dialog, but a dialog moves the focus itself one frame
    // after mounting and would take it back; the marker tells it to come here instead.
    const initialFocusAttribute = focusOnMount
        ? { [DIALOG_INITIAL_FOCUS_ATTRIBUTE]: '' }
        : undefined;
    const portalTarget = useModalPortalTarget();
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);
    // Track if user has manually deselected to prevent auto-reselection
    const hasManuallyDeselectedRef = useRef(false);
    const generatedId = useId();
    const resultsListId = `${generatedId}-employee-results`;
    const hasSeededReturnSearchRef = useRef(false);

    // Load current user ID on mount
    useMemo(() => {
        directoryService.getUserById('me').then((user) => {
            setCurrentUserId(user.id);
        });
    }, []);

    // Competency profiles are assigned on another page, which leaves every cached search
    // result carrying a stale hasProfile. Drop them on arrival rather than on the way out:
    // clearing them while a search is on screen makes its observers refetch at once, which
    // would just re-cache the pre-assignment answer. No search is active yet at mount.
    // Note: the directory query keys include params as a second element, so type: 'all'
    // is required to match all search variants.
    useEffect(() => {
        [
            DirectoryQueryKey.COMPANY_EMPLOYEES,
            DirectoryQueryKey.COMPANY_USERS,
            ASSESSMENT_EMPLOYEES_HIERARCHY_QUERY_KEY,
        ].forEach((key) =>
            queryClient.removeQueries({
                queryKey: [key],
                type: 'all',
            }),
        );
    }, [queryClient]);

    // Fetch preselected employee data if provided
    const { data: preselectedUser } = useGetUserById({
        userId: preselectedEmployeeId ?? '',
    });

    // Auto-select preselected employee when data is loaded (only on initial load)
    useEffect(() => {
        // Don't auto-select if user has manually deselected
        if (hasManuallyDeselectedRef.current) {
            return;
        }

        if (!preselectedUser || !preselectedEmployeeId || selectedEmployee) {
            return;
        }

        if (!preselectedUser.hasProfile) {
            if (!hasSeededReturnSearchRef.current) {
                hasSeededReturnSearchRef.current = true;
                setSearchQuery(getFullName(preselectedUser));
            }
            return;
        }

        const employee: AssessmentEmployee = {
            id: preselectedUser.id,
            firstName: preselectedUser.firstName,
            lastName: preselectedUser.lastName,
            avatarUrl: preselectedUser.profilePictureUrl ?? '',
            jobTitleId: preselectedUser.jobTitleId,
            hasProfile: true,
        };
        setSelectedEmployee(employee);
        onSelectEmployee(employee);
        setSearchQuery('');
    }, [preselectedUser, preselectedEmployeeId, selectedEmployee, onSelectEmployee]);

    const debouncedSearch = useDebounce(searchQuery, 400);
    const { data: permissions, isLoading: isPermissionsLoading } = useGetUserPermissions();
    const { data: employees = [], isLoading: isEmployeesLoading } = useGetAssessmentEmployees(
        debouncedSearch,
        {
            canManageAllDepartmentsPerformance: permissions?.canManageAllDepartmentsPerformance,
            includeHierarchy: !permissions?.canManageAllDepartmentsPerformance,
            // Fetched once the list is asked for, not on mount: a preselected employee never needs it.
            suggestBeforeSearch: isSuggestionListOpen,
        },
    );

    // Filter out current user when they have canManageAllDepartmentsPerformance permission
    const filteredEmployees = useMemo(() => {
        if (permissions?.canManageAllDepartmentsPerformance && currentUserId) {
            return employees.filter((emp) => emp.id !== currentUserId);
        }
        return employees;
    }, [employees, permissions?.canManageAllDepartmentsPerformance, currentUserId]);

    const isLoading = isEmployeesLoading || isPermissionsLoading;
    // const isLoading = false;
    // const employees: AssessmentEmployee[] = [
    //     {
    //         id: 'fc61a456-42f2-40c2-9973-0518e0d8f06c',
    //         firstName: '2',
    //         lastName: '2',
    //         avatarUrl: '',
    //     },
    // ];
    // The card follows the page's selection, so it goes when the page clears it. Derived rather than
    // cleared by an effect: on the first render the page has not heard of the preselected employee
    // yet, and an effect clearing the card would undo the preselection that ran just before it.
    const pickedEmployee = selectedEmployee?.id === selectedEmployeeId ? selectedEmployee : null;

    // A typed search always lists; the suggestions list only while it is open.
    const isAnySurfaceOpen = searchQuery.trim().length > 0 || isSuggestionListOpen;
    const showResults = isAnySurfaceOpen && !isLoading;

    const openSuggestions = () => {
        if (suggestOnFocus) {
            setIsSuggestionListOpen(true);
        }
    };

    // Only a gesture proposes employees. The focus this component moves itself — the field arriving
    // with the caret in it — answers no question the manager has asked yet, and a list unfurled over
    // a step the manager has not touched reads as a menu that opened by itself.
    const openSuggestionsOnFocus = () => {
        if (isMovingFocusItself.current) {
            return;
        }
        openSuggestions();
    };

    // Closes the suggestions once focus leaves both the field and the list: the list is portalled
    // out of the field's subtree, so a plain blur on the field would also fire on the way into it.
    const closeSuggestionsOnFocusOut = (event: FocusEvent) => {
        const nextFocus = event.relatedTarget as Node | null;
        if (fieldRef.current?.contains(nextFocus) || resultsListRef.current?.contains(nextFocus)) {
            return;
        }
        setIsSuggestionListOpen(false);
    };

    // `useAdaptiveDropdownDirection`, which `develop` wired here, is not called: the list is
    // portalled and `useAnchoredDropdownStyle` below already places it, flip included — which needs
    // the results' height, hence `contentRef`.
    // One style for the three surfaces — loading, results, no result — because they occupy the same
    // place one after the other. Recomputed as soon as any of them is about to show.
    const listStyle = useAnchoredDropdownStyle({
        anchorRef: fieldRef,
        isOpen: isAnySurfaceOpen,
        contentRef: resultsPopoverRef,
    });

    // The assignment page needs to know where the back action leads, since the manager did not
    // reach it through the administration menu.
    const goToAssignCompetencyProfile = (employeeId: string) => {
        const assignState = buildAssignProfileLocationState({
            employeeId,
            returnTo: RouteName.ManagerAssessment,
        });
        navigate({
            path: `${RouteName.CompetencyProfilesAdmin}${buildAssignProfileSearch(assignState)}`,
            state: assignState,
        });
    };

    const selectEmployee = (employee: AssessmentEmployee) => {
        onSelectEmployee(employee);
        setSelectedEmployee(employee);
        setSearchQuery('');
        setIsSuggestionListOpen(false);
    };

    return (
        <div
            className={'flex flex-col gap-7'}
            data-testid={'employee-selector'}
        >
            {/* Search input */}
            <div
                className={`relative transition-all duration-500 ${pickedEmployee ? 'max-w-[572px]' : 'max-w-[775px]'}`}
            >
                <div
                    ref={fieldRef}
                    className={`flex items-center gap-3 rounded-2xl border-2 px-4 transition-all duration-500 ${
                        selectedEmployee
                            ? 'border-base-100 bg-surface-field py-[6px]'
                            : 'border-primary bg-white py-4 shadow-[0_4px_12px_0_rgba(16,207,189,0.15)]'
                    }`}
                    data-testid={'employee-search'}
                >
                    <span style={{ flexShrink: 0 }}>
                        <Icon
                            name={'search-search-normal-1'}
                            color={pickedEmployee ? Colors.BASE_400 : Colors.PRIMARY}
                            size={'md'}
                        />
                    </span>

                    <input
                        {...getPasswordManagerAttributes()}
                        {...initialFocusAttribute}
                        ref={inputRef}
                        type={'text'}
                        role={'combobox'}
                        aria-expanded={showResults}
                        aria-controls={resultsListId}
                        aria-autocomplete={'list'}
                        placeholder={t('managerAssessmentSetup.searchEmployee')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={openSuggestionsOnFocus}
                        onClick={openSuggestions}
                        onBlur={closeSuggestionsOnFocusOut}
                        className={`placeholder-base-400 flex-1 border-none bg-transparent py-1 text-[14px] leading-6 transition-all duration-500 outline-none ${
                            pickedEmployee ? 'text-base-400' : 'font-medium text-gray-900'
                        }`}
                        data-testid={'employee-search-input'}
                    />

                    {searchQuery && (
                        <button
                            type={'button'}
                            onClick={() => setSearchQuery('')}
                            className={
                                'text-base-400 hover:text-base-600 flex items-center transition-colors'
                            }
                            aria-label={t('common.clearSearch')}
                            data-testid={'employee-search-clear'}
                        >
                            <Icon
                                name={'essential-close'}
                                color={Colors.BASE_400}
                                size={'sm'}
                            />
                        </button>
                    )}
                </div>

                {/* Loading results */}
                {isAnySurfaceOpen &&
                    isLoading &&
                    createPortal(
                        <div
                            style={listStyle}
                            className={
                                'flex justify-center rounded-lg border border-gray-200 bg-white p-4 shadow-lg'
                            }
                            data-testid={'employee-selector-loading'}
                        >
                            <Spinner size={'sm'} />
                        </div>,
                        portalTarget || document.body,
                    )}

                {/* Announces the outcome of a search: without it a screen-reader user has no way
                    of knowing that suggestions appeared, or how many. */}
                <div
                    role={'status'}
                    aria-live={'polite'}
                    className={'sr-only'}
                    data-testid={'employee-results-status'}
                >
                    {showResults
                        ? t('common.searchResultsCount', { count: filteredEmployees.length })
                        : ''}
                </div>

                {/* Dropdown results */}
                {showResults &&
                    filteredEmployees.length > 0 &&
                    createPortal(
                        <div
                            ref={resultsPopoverRef}
                            style={listStyle}
                            data-testid={'employee-results-popover'}
                        >
                            <div
                                ref={resultsListRef}
                                id={resultsListId}
                                role={'listbox'}
                                // Keeps the focus in the field while an option is pressed, so the
                                // field's blur cannot close the list before the click lands on it.
                                onMouseDown={(event) => event.preventDefault()}
                                onBlur={closeSuggestionsOnFocusOut}
                                aria-label={t('managerAssessmentSetup.searchEmployee')}
                                style={{ maxHeight: getResultsListMaxHeight(listStyle) }}
                                className={
                                    'scrollbar-glass overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg'
                                }
                            >
                                {filteredEmployees.map((employee) => (
                                    <EmployeeSearchResultRow
                                        key={employee.id}
                                        employee={employee}
                                        jobTitle={getJobTitle(employee.jobTitleId || '')}
                                        isSelected={selectedEmployeeId === employee.id}
                                        isMissingProfile={
                                            requireCompetencyProfile && !employee.hasProfile
                                        }
                                        onSelect={selectEmployee}
                                        onAssignProfile={goToAssignCompetencyProfile}
                                    />
                                ))}
                            </div>
                        </div>,
                        portalTarget || document.body,
                    )}

                {showResults &&
                    filteredEmployees.length === 0 &&
                    createPortal(
                        <div
                            style={listStyle}
                            className={
                                'rounded-lg border border-gray-200 bg-white p-4 text-center text-sm text-gray-500 shadow-lg'
                            }
                            data-testid={'employee-no-results'}
                        >
                            {t('managerAssessmentSetup.noEmployeesFound')}
                        </div>,
                        portalTarget || document.body,
                    )}
            </div>

            {/* Selected employee card */}
            {pickedEmployee && (
                <div
                    className={
                        'border-primary-500 relative flex max-w-[572px] items-center gap-5 rounded-2xl border bg-white/90 px-4 py-4 sm:px-12 sm:py-6 md:!px-[45px] md:!py-[21px]'
                    }
                    data-testid={'selected-employee-card'}
                >
                    <Avatar
                        src={getProfilePictureUrl(pickedEmployee.avatarUrl) || undefined}
                        alt={getFullName(pickedEmployee)}
                        initials={getInitials(pickedEmployee)}
                        bgColor={'purple'}
                        className={'!h-[48px] !w-[48px] sm:!h-[65px] sm:!w-[65px]'}
                    />

                    <div className={'flex flex-1 flex-col gap-0.5'}>
                        <span className={'text-md font-medium text-gray-900'}>
                            {getFullName(pickedEmployee)}
                        </span>
                        <span className={'text-[12px] text-gray-500'}>
                            {getJobTitle(pickedEmployee.jobTitleId || '')}
                        </span>
                    </div>

                    <button
                        type={'button'}
                        onClick={() => {
                            hasManuallyDeselectedRef.current = true;
                            setSelectedEmployee(null);
                            onDeselectEmployee();
                        }}
                        className={
                            'absolute top-3 right-3 flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-gray-100'
                        }
                        aria-label={t('managerAssessmentSetup.deselectEmployee')}
                        data-testid={'deselect-employee-button'}
                    >
                        <Icon
                            name={'essential-close'}
                            color={Colors.BASE_400}
                            size={'sm'}
                        />
                    </button>
                </div>
            )}
        </div>
    );
};
